import type { BuildingData, UnitData } from '../data/mockSpaceRevenue'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

export const BuildingVisualBreakdown = ({ buildings }: { buildings: BuildingData[] }) => (
  <div className="space-y-6 mb-8">
    <h2 className="text-lg font-semibold tracking-tight">Building Visual Breakdown</h2>
    <div className="border rounded-lg p-6 bg-card text-card-foreground shadow-sm space-y-8">
        {buildings.map(building => (
            <BuildingRow key={building.id} building={building} />
        ))}
        
        <div className="flex flex-wrap gap-4 text-xs text-muted-foreground pt-4 border-t border-dashed">
             <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-emerald-500" /> Active Lease</div>
             <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-orange-400" /> Underpriced</div>
             <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-blue-500" /> Transient</div>
             <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-yellow-400" /> Expiring ≤90d</div>
             <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-gray-100 border border-gray-300" /> Vacant</div>
             <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-slate-800" /> Maintenance</div>
        </div>
    </div>
  </div>
)

const BuildingRow = ({ building }: { building: BuildingData }) => {
    // Determine the total width scale based on sqft (optional: normalize to max building width)
    // For now, we use flex basis proportional to sqft
    
    return (
        <div className="space-y-3">
             <div className="flex items-baseline justify-between">
                <div className="flex items-baseline gap-3">
                    <h3 className="font-medium text-base">{building.name}</h3>
                    <span className="text-sm text-muted-foreground hidden sm:inline">
                        {building.total_sqft.toLocaleString()} sqft · {building.occupancy_percent}% occupied
                    </span>
                </div>
                <div className="text-sm font-medium">
                     ${building.contracted_revenue.toLocaleString()}/mo
                </div>
            </div>

            <div className="flex gap-px w-full h-24 bg-gray-100 rounded-lg overflow-hidden border border-slate-200">
                <TooltipProvider>
                    {building.units.map((unit) => (
                        <VisualUnitBlock key={unit.id} unit={unit} totalSqft={building.total_sqft} />
                    ))}
                </TooltipProvider>
            </div>
        </div>
    )
}

const VisualUnitBlock = ({ unit, totalSqft }: { unit: UnitData, totalSqft: number }) => {
    const widthPercent = (unit.sqft / totalSqft) * 100
    
    let bgColor = 'bg-gray-100' // default vacant
    let borderColor = 'border-gray-200'
    let textColor = 'text-gray-400'
    
    switch(unit.status) {
        case 'leased': 
            bgColor = 'bg-emerald-100 hover:bg-emerald-200'
            borderColor = 'border-emerald-200'
            textColor = 'text-emerald-800'
            break
        case 'underpriced':
             bgColor = 'bg-orange-100 hover:bg-orange-200'
             borderColor = 'border-orange-200'
             textColor = 'text-orange-800'
             break
        case 'transient':
             bgColor = 'bg-blue-100 hover:bg-blue-200'
             borderColor = 'border-blue-200'
             textColor = 'text-blue-800'
             break
        case 'expiring':
             bgColor = 'bg-yellow-100 hover:bg-yellow-200'
             borderColor = 'border-yellow-200'
             textColor = 'text-yellow-800'
             break
        case 'maintenance':
             bgColor = 'bg-slate-800 hover:bg-slate-700'
             borderColor = 'border-slate-800'
             textColor = 'text-slate-100'
             break
        case 'vacant':
             bgColor = 'bg-white hover:bg-gray-50'
             borderColor = 'border-gray-200'
             textColor = 'text-gray-400'
             break
    }

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <div 
                    className={cn(
                        "h-full flex flex-col items-center justify-center p-1 border-r last:border-r-0 transition-colors cursor-pointer relative group",
                        bgColor, borderColor
                    )}
                    style={{ width: `${widthPercent}%` }}
                >
                    <div className={cn("text-xs font-bold truncate w-full text-center", textColor)}>
                        {unit.unit_code}
                    </div>
                    {unit.aircraft_tail ? (
                        <div className={cn("text-[10px] truncate w-full text-center font-medium opacity-80", textColor)}>
                            {unit.aircraft_tail}
                        </div>
                    ) : (
                         <div className="text-[10px] text-center w-full opacity-50 font-medium">
                            {unit.status === 'vacant' ? 'VACANT' : unit.status}
                         </div>
                    )}
                    
                    {/* Status Dot for leased/active */}
                     {(unit.status === 'leased' || unit.status === 'underpriced' || unit.status === 'transient') && (
                        <div className={cn(
                            "absolute bottom-2 w-1.5 h-1.5 rounded-full",
                             unit.status === 'leased' ? "bg-emerald-500" : 
                             unit.status === 'underpriced' ? "bg-orange-500" :
                             "bg-blue-500"
                        )} />
                     )}
                </div>
            </TooltipTrigger>
            <TooltipContent>
                <div className="text-xs space-y-1">
                    <p className="font-bold">{unit.building_name} - {unit.unit_code}</p>
                    <p>{unit.sqft.toLocaleString()} sqft · {unit.unit_type}</p>
                    <div className="h-px bg-border my-1" />
                    {unit.tenant_name ? (
                        <>
                            <p><span className="text-muted-foreground">Tenant:</span> {unit.tenant_name}</p>
                             <p><span className="text-muted-foreground">Aircraft:</span> {unit.aircraft_tail} ({unit.aircraft_type})</p>
                             {unit.monthly_rate && <p><span className="text-muted-foreground">Rate:</span> ${unit.monthly_rate}/mo</p>}
                             {unit.contract_end_date && <p><span className="text-muted-foreground">Expires:</span> {unit.contract_end_date}</p>}
                        </>
                    ) : (
                        <p className="italic text-muted-foreground">No active lease</p>
                    )}
                     <p className="uppercase text-[10px] font-bold mt-1 tracking-wider text-muted-foreground">{unit.status}</p>
                </div>
            </TooltipContent>
        </Tooltip>
    )
}
